// Ship 2 — Firm Feed tab.  Thin wrapper around the existing activity-feed
// endpoint.  Admin + can_view_all_leads users get firm-wide scope; other
// staff get their own activity only (backend applies matter-visibility +
// user-scope automatically per activityFeed.js).
//
// Reuses the same UI shape as pages/ActivityFeed.js but wired for embedding
// inside the Dashboard tab bar (no page-level header / pop-out button).

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import api from '../services/api'
import { SkeletonTable, LoadingRegion } from './Skeleton'
import EmptyState from './EmptyState'

const ICON = {
  time_entry:    '⏱',
  invoice_paid:  '💰',
  client_new:    '👤',
  matter_new:    '📁',
  trust_deposit: '🏦',
  comm:          '📞',
}

const WINDOW_OPTIONS = [
  { hours: 24,  label: 'Last 24 hours' },
  { hours: 168, label: 'Last 7 days' },
  { hours: 720, label: 'Last 30 days' },
]

export default function FirmFeedTab({ user }) {
  const isAdmin = user?.role === 'admin' || user?.permissions?.is_admin === true
  const canViewAllLeads = isAdmin || user?.permissions?.can_view_all_leads === true

  // Persisted window preference (matches the standalone ActivityFeed page's
  // localStorage key so the two views stay in sync per user).
  const [windowHours, setWindowHours] = useState(() => {
    try { return parseInt(localStorage.getItem('lextrack_activity_window'), 10) || (24 * 7) } catch { return 24 * 7 }
  })
  useEffect(() => {
    try { localStorage.setItem('lextrack_activity_window', String(windowHours)) } catch {}
  }, [windowHours])

  const [userFilter, setUserFilter] = useState('')
  const [teamList, setTeamList] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError('')
    const params = new URLSearchParams({ hours: String(windowHours) })
    if (canViewAllLeads && userFilter) params.set('user_id', userFilter)
    api.get(`/api/activity-feed?${params.toString()}`)
      .then((res) => { if (!cancelled) setData(res.data) })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'Could not load activity feed') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [windowHours, userFilter, canViewAllLeads])

  // Load team list for the user-filter dropdown — only when the caller can
  // see other users' activity firm-wide.
  useEffect(() => {
    if (!canViewAllLeads) return
    api.get('/api/team').then((r) => setTeamList(r.data || [])).catch(() => setTeamList([]))
  }, [canViewAllLeads])

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">🌊 Firm Feed</h3>
          <p className="text-xs text-gray-500">
            {canViewAllLeads
              ? 'Recent activity across the firm (respects per-matter visibility)'
              : 'Your recent activity'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={windowHours}
            onChange={(e) => setWindowHours(parseInt(e.target.value, 10))}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.hours} value={o.hours}>{o.label}</option>
            ))}
          </select>
          {canViewAllLeads && (
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
            >
              <option value="">Firm-wide</option>
              {teamList.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )}
          <Link
            to="/activity-feed"
            className="text-xs text-primary-600 hover:underline px-2 py-1.5"
          >
            Full feed →
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <LoadingRegion label="Loading activity feed">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <SkeletonTable rows={10} cols={4} />
          </div>
        </LoadingRegion>
      )}

      {!loading && data && data.count === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          {/* The window labels already start with "Last" ("in the last 7
              days"); the sentence used to add its own ("the last last"). */}
          <EmptyState
            icon="📰"
            title={userFilter ? 'No activity for this user' : 'No activity in the window'}
            description={`No records found in the ${WINDOW_OPTIONS.find((o) => o.hours === windowHours)?.label.toLowerCase() || 'selected window'}. Try a wider window.`}
            action={{ label: 'Show last 30 days', onClick: () => setWindowHours(720) }}
          />
        </div>
      )}

      {!loading && data && data.count > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 divide-y divide-gray-100">
          {data.events.map((e, i) => (
            <Link
              key={i}
              to={e.ref_url || '#'}
              className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50"
            >
              <span className="shrink-0 text-2xl">{ICON[e.type] || '•'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 truncate">{e.description}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {e.when_at && format(new Date(e.when_at), 'MMM d, yyyy h:mm a')}
                  {e.matter_title && <> · {e.matter_title}</>}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
